import { API_BASE_URL as BASE } from '@/lib/apiBase'
import { authFetch, authHeaders, handleUnauthorized } from '@/lib/authFetch'
import type {
  IsbtDateFormat,
  IsbtUse,
  LabelType,
  PrinterFamily,
  ZebraConnection,
  ZebraTemplateStatus,
} from '@/lib/labelPrinting'

/** The fixed choices the forms offer, from the server's own lists. */
export interface LabelPrintingOptions {
  isbtSizes: string[]
  zebraStocks: string[]
  zebraResolutions: string[]
  uses: IsbtUse[]
  dateFormats: IsbtDateFormat[]
  /** Department / location values already used in this tenant. */
  locations: string[]
  placeholders: ZebraPlaceholder[]
}

/** A Zebra blank such as `lot_num` that the backend can fill, and where its value comes from. */
export interface ZebraPlaceholder {
  name: string
  filledFrom: string
  /** A made-up value, used only to draw a preview. */
  sample: string
}

/** An ISBT label design: Smart CGT stores only the `.lbl` file name. */
export interface IsbtTemplate {
  id: string
  displayName: string
  fileName: string
  /** null until the size is confirmed; such a template cannot be approved. */
  labelSize: string | null
  use: IsbtUse
  dateFormat: IsbtDateFormat
  approvedBy: string | null
  approvedAt: string | null
  active: boolean
  createdAt: string
  updatedAt: string
}

export interface IsbtTemplatePayload {
  displayName: string
  fileName: string
  labelSize: string | null
  use: IsbtUse
  dateFormat: IsbtDateFormat
}

/** An ISBT printer, reached by its queue name on the print computer. */
export interface IsbtPrinter {
  id: string
  displayName: string
  queueName: string
  loadedStock: string
  location: string | null
  signatureRequired: boolean
  active: boolean
  /** Includes links to templates retired since (`active: false`). */
  allowedTemplates: {
    id: string
    displayName: string
    fileName: string
    labelSize: string | null
    active: boolean
    approved: boolean
  }[]
  createdAt: string
  updatedAt: string
}

export interface IsbtPrinterPayload {
  displayName: string
  queueName: string
  loadedStock: string
  location?: string | null
  signatureRequired?: boolean
  allowedTemplateIds: string[]
}

export interface TestPrintPayload {
  templateId: string
  queueName: string
  loadedStock: string
  /** Only for a saved printer: a test print works before the printer is saved. */
  printerId?: string
}

/** 202 from the print relay: handed off, not proof that it printed. */
export interface TestPrintResult {
  printJobId: string
  status: string
  acceptedAt: string
}

/** One version of a Zebra layout. Versions are never edited once approved. */
export interface ZebraVersion {
  id: string
  version: number
  body: string
  /** The placeholder names the body uses. */
  placeholders: string[]
  status: 'draft' | 'approved'
  /** null: applies from the start. */
  effectiveFrom: string | null
  /** The same moment as the tenant's calendar day, `YYYY-MM-DD`: what the screens show. */
  effectiveDate: string | null
  reason: string
  changedBy: string
  changedAt: string
  approvedBy: string | null
  approvedAt: string | null
}

/**
 * A Zebra label design. Its name, size and resolution belong to the template;
 * the layout belongs to a version. At most one draft is open at a time.
 */
export interface ZebraTemplate {
  id: string
  name: string
  labelStock: string
  resolution: string
  active: boolean
  status: ZebraTemplateStatus
  /** The approved version in use now, if any. */
  liveVersion: ZebraVersion | null
  /** An approved version whose first day has not started yet. */
  nextVersion: ZebraVersion | null
  /** The draft waiting for approval, if any. */
  draftVersion: ZebraVersion | null
  /** Newest first. */
  versions: ZebraVersion[]
  createdAt: string
  updatedAt: string
}

export interface ZebraTemplateCreatePayload {
  name: string
  labelStock: string
  resolution: string
  body: string
  /** `YYYY-MM-DD`. */
  effectiveFrom: string
  reason: string
}

/** Creates the draft, or replaces the open one. */
export interface ZebraDraftPayload {
  body: string
  /** `YYYY-MM-DD`. */
  effectiveFrom: string
  reason: string
  /**
   * `changedAt` of the draft the form started from, or null when it started
   * from no draft. The server answers 409 if the draft changed since.
   */
  baseChangedAt: string | null
}

/** The draft the approver saw; the server answers 409 if it changed since. */
export interface ZebraApprovePayload {
  versionId: string
  changedAt: string
}

export interface ZebraPrinter {
  id: string
  displayName: string
  connection: ZebraConnection
  host: string
  port: number
  serverQueueName: string | null
  resolution: string
  loadedStock: string
  location: string | null
  signatureRequired: boolean
  active: boolean
  /** Includes links to templates retired since (`active: false`). */
  allowedTemplates: {
    id: string
    name: string
    labelStock: string
    resolution: string
    active: boolean
    approved: boolean
  }[]
  createdAt: string
  updatedAt: string
}

export interface ZebraPrinterPayload {
  displayName: string
  connection: ZebraConnection
  host: string
  port: number
  serverQueueName: string | null
  resolution: string
  loadedStock: string
  location: string | null
  signatureRequired: boolean
  allowedTemplateIds: string[]
}

/** Which kind of printer a label type's print buttons send to. */
export interface LabelTypeSetting {
  labelType: LabelType
  printerFamily: PrinterFamily
  updatedBy: string
  updatedAt: string
  /** Active printers of `printerFamily` in this tenant. */
  activePrinters: number
}

// ─── Printing a real label from a print button ────────────────────────────────

/** What a print button prints. A location is printed from its lot. */
export type PrintSubject = 'product' | 'lot' | 'location' | 'equipment'

/** The Label Printing choices of a product label. Empty lines are "". */
export interface PrintProductChoices {
  labelType?: string
  bloodType?: string
  specialMessage?: string
  intendedUse?: string
  donationType?: string
  freeText?: string[]
}

export interface PrintContextPayload {
  subject: PrintSubject
  ids: string[]
  selections?: PrintProductChoices
}

/** A template a printer may print now. */
export interface PrintTemplateChoice {
  id: string
  name: string
  size: string | null
  resolution: string | null
  /** Zebra: the live version that prints. */
  version: number | null
  /** Zebra: the live layout, for the preview. */
  body: string | null
  /** ISBT: the .lbl file on the print computer. */
  fileName: string | null
}

export interface PrintPrinterChoice {
  id: string
  family: PrinterFamily
  displayName: string
  location: string | null
  loadedStock: string
  resolution: string | null
  /** Where the job goes, in words: a queue name or host:port. */
  destination: string
  signatureRequired: boolean
  /** Empty when nothing approved fits its loaded label. */
  templates: PrintTemplateChoice[]
  suggestedTemplateId: string | null
}

export interface PrintLabelChoice {
  id: string
  title: string
  subtitle: string
  facts: { label: string; value: string }[]
  /** Raw values for every Zebra placeholder. */
  zebraValues: Record<string, string>
  /** Why this label cannot go to this printer kind. Empty when it can. */
  problems: string[]
  /** What the print service may refuse. Shown, but the label is still sent. */
  warnings: string[]
}

export interface PrintContext {
  subject: PrintSubject
  labelType: LabelType
  printerFamily: PrinterFamily
  printers: PrintPrinterChoice[]
  labels: PrintLabelChoice[]
}

export interface PrintLabelsPayload extends PrintContextPayload {
  printerId: string
  templateId: string
  /** Products: the recorded label of each id, in the same order. */
  labelIds?: string[]
  signingToken?: string
  signatureMeaning?: string
}

export type PrintOutcome = 'accepted' | 'unavailable' | 'rejected' | 'timeout' | 'failed' | 'skipped'

export interface PrintResult {
  id: string
  title: string
  printJobId: string | null
  outcome: PrintOutcome
  acceptedAt: string | null
  message: string | null
}

export interface PrintLabelsResult {
  printer: { id: string; displayName: string; family: PrinterFamily }
  results: PrintResult[]
}

/** A failed call, with its HTTP status: test print tells 503 apart from the rest. */
export class LabelPrintingApiError extends Error {
  readonly status: number

  constructor(message: string, status: number) {
    super(message)
    this.name = 'LabelPrintingApiError'
    this.status = status
  }
}

async function request<T>(path: string, init?: RequestInit): Promise<T> {
  const res = await authFetch(`${BASE}/label-printing${path}`, {
    ...init,
    headers: authHeaders(init?.headers),
  })
  if (!res.ok) {
    handleUnauthorized(res.status)
    const text = await res.text().catch(() => res.statusText)
    let message = text
    try {
      const body = JSON.parse(text) as { message?: string | string[] }
      // class-validator answers with an array of messages; show them all. Each
      // already ends with a period, so a space is the separator.
      message = Array.isArray(body.message) ? body.message.join(' ') : (body.message ?? text)
    } catch {
      // keep raw text
    }
    throw new LabelPrintingApiError(
      message || `API ${init?.method ?? 'GET'} /label-printing${path} failed (${res.status})`,
      res.status,
    )
  }
  if (res.status === 204) return undefined as T
  return res.json() as Promise<T>
}

const json = (method: string, body: unknown): RequestInit => ({ method, body: JSON.stringify(body) })

// Tenant-scoped: authHeaders injects X-Tenant-Id for the selected tenant.
// Nothing here deletes: retire and reactivate are PATCH { active }.
export const labelPrintingApi = {
  options: () => request<LabelPrintingOptions>('/options'),

  listIsbtTemplates: () => request<IsbtTemplate[]>('/isbt-templates'),
  createIsbtTemplate: (payload: IsbtTemplatePayload) =>
    request<IsbtTemplate>('/isbt-templates', json('POST', payload)),
  updateIsbtTemplate: (id: string, payload: Partial<IsbtTemplatePayload> & { active?: boolean }) =>
    request<IsbtTemplate>(`/isbt-templates/${id}`, json('PATCH', payload)),
  approveIsbtTemplate: (id: string) =>
    request<IsbtTemplate>(`/isbt-templates/${id}/approve`, { method: 'POST' }),

  listIsbtPrinters: () => request<IsbtPrinter[]>('/isbt-printers'),
  createIsbtPrinter: (payload: IsbtPrinterPayload) =>
    request<IsbtPrinter>('/isbt-printers', json('POST', payload)),
  updateIsbtPrinter: (id: string, payload: Partial<IsbtPrinterPayload> & { active?: boolean }) =>
    request<IsbtPrinter>(`/isbt-printers/${id}`, json('PATCH', payload)),
  testPrint: (payload: TestPrintPayload) =>
    request<TestPrintResult>('/isbt-printers/test-print', json('POST', payload)),

  listZebraTemplates: () => request<ZebraTemplate[]>('/zebra-templates'),
  createZebraTemplate: (payload: ZebraTemplateCreatePayload) =>
    request<ZebraTemplate>('/zebra-templates', json('POST', payload)),
  saveZebraDraft: (id: string, payload: ZebraDraftPayload) =>
    request<ZebraTemplate>(`/zebra-templates/${id}/draft`, json('PUT', payload)),
  approveZebraTemplate: (id: string, payload: ZebraApprovePayload) =>
    request<ZebraTemplate>(`/zebra-templates/${id}/approve`, json('POST', payload)),
  /** Rename, retire or reactivate. The layout changes only through a draft. */
  updateZebraTemplate: (id: string, payload: { name?: string; active?: boolean }) =>
    request<ZebraTemplate>(`/zebra-templates/${id}`, json('PATCH', payload)),

  listZebraPrinters: () => request<ZebraPrinter[]>('/zebra-printers'),
  createZebraPrinter: (payload: ZebraPrinterPayload) =>
    request<ZebraPrinter>('/zebra-printers', json('POST', payload)),
  updateZebraPrinter: (id: string, payload: Partial<ZebraPrinterPayload> & { active?: boolean }) =>
    request<ZebraPrinter>(`/zebra-printers/${id}`, json('PATCH', payload)),

  /** Every label type, in a fixed order. */
  listLabelTypes: () => request<LabelTypeSetting[]>('/label-types'),
  updateLabelType: (labelType: LabelType, printerFamily: PrinterFamily) =>
    request<LabelTypeSetting>(`/label-types/${labelType}`, json('PUT', { printerFamily })),

  /** The printers, templates and values the print popup shows. */
  printContext: (payload: PrintContextPayload) =>
    request<PrintContext>('/jobs/context', json('POST', payload)),
  /** One job per label. "accepted" means handed off, not printed. */
  print: (payload: PrintLabelsPayload) => request<PrintLabelsResult>('/jobs', json('POST', payload)),
}
